import {
  addDisplayName,
  addHandlers,
  addProps,
  addState,
  flowMax,
} from 'ad-hok'
import cx from 'classnames'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { addNavigate } from 'shared/src/utils/addNavigate'
import { trackEvent } from 'shared/src/utils/analytics'
import { typedAs } from 'shared/src/utils/typedAs'

import chevron from 'assets/notification-action-chevron.svg'
import appIcon from 'assets/rodeo-app-icon.svg'
import closeIcon from 'assets/close-icon-auth.svg'
import { sansSerifFontFamily } from '../../utils/styles'
import { NOTIFICATION_Z_INDEX } from '../../utils/zIndex'
import { Button } from '../Button'
import { Notification as NotificationInterface } from './index'

const useStyles = createUseStyles({
  container: {
    zIndex: NOTIFICATION_Z_INDEX,
    position: 'fixed',
    minWidth: 320,
    maxWidth: 'calc(428px - 46px)',
    width: 'calc(100vw - 46px)',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  top: {
    top: 0,
    padding: '32px 23px',
    background:
      'conic-gradient(from 78.27deg at 82.95% -17.19%, #887764 -0.52deg, #9C876E 56.3deg, #D99B71 158.54deg, #CDAA7E 222.52deg, #C7976E 279.7deg, #887764 359.48deg, #9C876E 416.3deg)',
  },
  bottom: {
    bottom: 0,
    backgroundColor: 'rgba(61, 60, 60, 0.72)',
    backdropFilter: 'blur(4px)',
    paddingLeft: 18,
    paddingRight: 23,
    paddingTop: 22,
    paddingBottom: 22,
    maxWidth: 'calc(428px - 41px)',
  },
  bottomTextContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  textTop: {
    fontFamily: sansSerifFontFamily,
    fontSize: 14,
    fontWeight: 700,
    color: 'white',
  },
  textBottom: {
    fontFamily: sansSerifFontFamily,
    fontSize: 16,
    fontWeight: 700,
    color: 'rgba(255, 255, 255, 0.80)',
    letterSpacing: 0.16,
    lineHeight: '120%',
    maxWidth: 250,
  },
  actionTop: {
    fontFamily: sansSerifFontFamily,
    fontSize: 14,
    fontWeight: 800,
    color: 'white',
    letterSpacing: '0.06em',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  actionBottom: {
    height: 36,
    backgroundColor: '#1D1B1A',
    borderRadius: 10.5,
    paddingLeft: 21,
    paddingRight: 21,
    fontFamily: sansSerifFontFamily,
    fontSize: 9.25,
    fontWeight: 800,
    color: 'white',
    letterSpacing: '0.1em',
  },
  chevron: {
    marginLeft: 8,
  },
  closeBtn: {
    top: '-15px',
    right: '-5px',
    display: 'flex',
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    height: 'fit-content',
  },
  closeBtnImg: {
    width: '32px',
  },
})

interface Props {
  notification: NotificationInterface
  onAction: () => void
}

export const Notification: FC<Props> = flowMax(
  addState('touchStart', 'setTouchStart', typedAs<number | null>(null)),
  addState('touchEnd', 'setTouchEnd', typedAs<number | null>(null)),
  addDisplayName('Notification'),
  addNavigate,
  addHandlers({
    onTouchStart:
      ({ setTouchEnd, setTouchStart }) =>
      (e: any) => {
        setTouchEnd(null)
        setTouchStart(e.targetTouches[0].clientY)
      },
    onTouchMove:
      ({ setTouchEnd }) =>
      (e: any) => {
        setTouchEnd(e.targetTouches[0].clientY)
      },
    onTouchEnd:
      ({ touchStart, touchEnd, notification: { sticky, style }, onAction }) =>
      () => {
        const minSwipeDistance = 50
        if (!touchStart || !touchEnd) return
        const distance = touchStart - touchEnd
        const isTopSwipe = distance > minSwipeDistance
        const isBottomtSwipe = distance < -minSwipeDistance
        if (
          sticky &&
          ((style === 'top' && isTopSwipe) ||
            (style === 'bottom' && isBottomtSwipe))
        ) {
          onAction()
        }
      },
    onClick:
      ({ notification: { button }, navigate, onAction }) =>
      () => {
        if (button) {
          button.onClick?.()

          if (button.onClickAnalyticsEvent) {
            trackEvent(
              button.onClickAnalyticsEvent.options,
              button.onClickAnalyticsEvent.params
            )
          }

          if (button.external) {
            window.open(button.navigateTo, '_blank')
          } else {
            navigate(button.navigateTo, { state: button.state })
          }
        }
        onAction()
      },
    onClose:
      ({ onAction }) =>
      () => {
        onAction()
      },
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    notification: { text, button, style, sticky },
    onTouchStart,
    onTouchEnd,
    onTouchMove,
    onClick,
    onClose,
    classes,
  }) => (
    <div
      className={cx(
        classes.container,
        style === 'top' ? classes.top : classes.bottom
      )}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div className={classes.bottomTextContainer}>
        {style === 'bottom' && <img src={appIcon} alt="" />}
        <div className={style === 'top' ? classes.textTop : classes.textBottom}>
          {text}
        </div>
      </div>
      {button && (
        <Button
          onClick={onClick}
          className={style === 'top' ? classes.actionTop : classes.actionBottom}
        >
          {button.title}
          {style === 'top' && (
            <img src={chevron} alt="" className={classes.chevron} />
          )}
        </Button>
      )}
      {sticky && (
        <Button onClick={onClose} className={classes.closeBtn}>
          <img src={closeIcon} alt="" className={classes.closeBtnImg} />
        </Button>
      )}
    </div>
  )
)
